/** Local image comparison; finish details never change product selections. */
if (!customElements.get('epic-finish-slider')) {
  class EpicFinishSlider extends HTMLElement {
    #abortController;
    #stage;
    #range;
    #wide;
    #pointerId = null;
    #openPanel = null;
    #trigger = null;

    connectedCallback() {
      this.#abortController?.abort();
      this.#stage = this.querySelector('[data-comparison-stage]');
      this.#range = this.querySelector('[data-comparison-range]');
      if (!this.#stage || !this.#range) return;
      this.#abortController = new AbortController();
      const options = { signal: this.#abortController.signal };
      this.#wide = window.matchMedia('(min-width: 990px)');
      this.#wide.addEventListener('change', this.#onBreakpointChange, options);
      this.#range.addEventListener('input', this.#onInput, options);
      this.#stage.addEventListener('pointerdown', this.#onPointerDown, options);
      this.#stage.addEventListener('pointermove', this.#onPointerMove, options);
      this.#stage.addEventListener('pointerup', this.#onPointerEnd, options);
      this.#stage.addEventListener('pointercancel', this.#onPointerEnd, options);
      this.#stage.addEventListener('lostpointercapture', this.#onPointerEnd, options);
      this.addEventListener('click', this.#onClick, options);
      this.addEventListener('keydown', this.#onKeyDown, options);
      document.addEventListener('shopify:block:select', this.#onBlockSelect, options);
      document.addEventListener('shopify:block:deselect', this.#onBlockDeselect, options);
      this.#closeDetails(false);
      this.#update(this.#range.value);
      this.setAttribute('data-ready', '');
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#endDrag();
      this.#closeDetails(false);
      this.removeAttribute('data-ready');
    }

    #update(value) {
      const position = Math.min(100, Math.max(0, Number(value) || 0));
      this.#range.value = String(position);
      this.style.setProperty('--epic-comparison-position', `${position}%`);
      this.#range.setAttribute('aria-valuetext', `${this.dataset.leftLabel}: ${position}%; ${this.dataset.rightLabel}: ${100 - position}%`);
    }

    #onInput = () => this.#update(this.#range.value);

    #updateFromPointer(event) {
      const rect = this.#stage.getBoundingClientRect();
      if (rect.width > 0) this.#update(Math.round(((event.clientX - rect.left) / rect.width) * 100));
    }

    #onPointerDown = (event) => {
      if (this.#openPanel || !event.isPrimary || event.button !== 0 || this.#pointerId !== null) return;
      if (event.target.closest('button, a, input, [data-comparison-details]')) return;
      if (event.pointerType === 'mouse') event.preventDefault();
      this.#pointerId = event.pointerId;
      this.#stage.setPointerCapture(event.pointerId);
      this.#range.focus({ preventScroll: true });
      this.setAttribute('data-dragging', '');
      this.#updateFromPointer(event);
    };

    #onPointerMove = (event) => {
      if (event.pointerId === this.#pointerId) this.#updateFromPointer(event);
    };

    #endDrag() {
      const pointerId = this.#pointerId;
      this.#pointerId = null;
      this.removeAttribute('data-dragging');
      if (pointerId !== null && this.#stage?.hasPointerCapture(pointerId)) this.#stage.releasePointerCapture(pointerId);
    }

    #onPointerEnd = (event) => {
      if (event.pointerId === this.#pointerId) this.#endDrag();
    };

    #openDetails(panel, focus = true) {
      if (this.#wide.matches || !panel) return;
      this.#endDrag();
      this.#closeDetails(false);
      this.#openPanel = panel;
      this.#trigger = Array.from(this.querySelectorAll('[data-details-trigger]')).find(button => button.getAttribute('aria-controls') === panel.id);
      panel.setAttribute('data-open', '');
      this.#trigger?.setAttribute('aria-expanded', 'true');
      this.setAttribute('data-details-open', '');
      this.#range.disabled = true;
      if (focus) panel.querySelector('[data-details-close]')?.focus({ preventScroll: true });
    }

    #closeDetails(restoreFocus = true) {
      const trigger = this.#trigger;
      this.querySelectorAll('[data-comparison-details]').forEach(panel => panel.removeAttribute('data-open'));
      this.querySelectorAll('[data-details-trigger]').forEach(button => button.setAttribute('aria-expanded', 'false'));
      this.removeAttribute('data-details-open');
      if (this.#range) this.#range.disabled = false;
      this.#openPanel = null;
      this.#trigger = null;
      if (restoreFocus) trigger?.focus({ preventScroll: true });
    }

    #onClick = (event) => {
      const trigger = event.target.closest('[data-details-trigger]');
      if (trigger) {
        const panel = Array.from(this.querySelectorAll('[data-comparison-details]')).find(item => item.id === trigger.getAttribute('aria-controls'));
        this.#openDetails(panel);
      } else if (event.target.closest('[data-details-close]')) {
        this.#closeDetails();
      }
    };

    #onKeyDown = (event) => {
      if (event.key === 'Escape' && this.#openPanel) {
        event.preventDefault();
        this.#closeDetails();
      }
    };

    #onBreakpointChange = () => {
      const focusedPanel = document.activeElement?.closest('[data-comparison-details]');
      const focusedTrigger = document.activeElement?.closest('[data-details-trigger]');
      this.#endDrag();
      this.#closeDetails(false);
      if (this.#wide.matches) {
        if ((focusedPanel && this.contains(focusedPanel)) || (focusedTrigger && this.contains(focusedTrigger))) {
          this.#range.focus({ preventScroll: true });
        }
      } else if (focusedPanel && this.contains(focusedPanel)) {
        this.#openDetails(focusedPanel);
      }
    };

    #onBlockSelect = (event) => {
      const panel = Array.from(this.querySelectorAll('[data-comparison-details]')).find(item => item.dataset.blockId === event.detail?.blockId);
      this.#openDetails(panel, false);
    };

    #onBlockDeselect = (event) => {
      if (this.#openPanel?.dataset.blockId === event.detail?.blockId) this.#closeDetails(false);
    };
  }
  customElements.define('epic-finish-slider', EpicFinishSlider);
}
